import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import styled from 'styled-components';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import MainLayout from '../../../layouts/MainLayout';
import logger from '../../../utils/logger';
import protocoloService from '../services/protocoloService';
import { authService, documentoAnexoService } from '../../../services';
import { Alert } from '../../../components/ui/Alert/Alert';
import StepIndicator from '../components/StepIndicator';
import IniciarProtocolo from '../components/IniciarProtocolo';
import DocumentUpload from '../components/DocumentUpload';
import SuccessScreen from '../components/SuccessScreen';

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto; /* Libera scroll vertical para toda a página */
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  /* Elemento decorativo: faixa azul no topo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  padding: 120px 2rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${colors.darkBlue};
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: ${colors.textMuted};
  font-size: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ProtocoloOnlinePage = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [solicitacaoId, setSolicitacaoId] = useState(null);
  const [requerimentoId, setRequerimentoId] = useState(null);
  // Só é preenchido depois que o Protocolo/DocumentoProtocolo final existe (pós-finalizar).
  const [documentoProtocoloId, setDocumentoProtocoloId] = useState(null);
  const [protocoloData, setProtocoloData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const alertRef = useRef(null);

  // Recupera o rascunho salvo no localStorage caso a página seja recarregada na etapa 2
  useEffect(() => {
    if (user) {
      const savedProtocolo = localStorage.getItem('protocolo_rascunho');
      if (savedProtocolo) {
        try {
          const protocolo = JSON.parse(savedProtocolo);
          if (protocolo.solicitacaoId) {
            setSolicitacaoId(protocolo.solicitacaoId);
            setRequerimentoId(protocolo.requerimentoId || null);
            setDocumentoProtocoloId(protocolo.documentoProtocoloId || null);
            setCurrentStep(2);
          }
        } catch (e) {
          logger.error('Erro ao ler protocolo salvo:', e);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      window.location.href = '/login';
    }
  }, [user]);

  useEffect(() => {
    if (error) {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Cria só o Requerimento (rascunho, situacao 4 = Pendente). O Protocolo final
  // só nasce quando a pessoa concluir o envio dos documentos, pra nunca sobrar
  // um protocolo sem anexo.
  const handleIniciarProtocolo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await protocoloService.criarProtocoloOnline(user);
      const result = response.data || response;
      const novoSolicitacaoId = result?.solicitacaoId;
      const novoRequerimentoId = result?.requerimentoId;

      if (novoSolicitacaoId) {
        setSolicitacaoId(novoSolicitacaoId);
        setRequerimentoId(novoRequerimentoId || null);
        setProtocoloData(result);
        setCurrentStep(2);
        localStorage.setItem('protocolo_rascunho', JSON.stringify({
          solicitacaoId: novoSolicitacaoId,
          requerimentoId: novoRequerimentoId || null
        }));
      } else {
        setError('Erro ao obter ID do requerimento.');
      }
    } catch (err) {
      setError(err.message || 'Erro ao criar requerimento');
    } finally {
      setLoading(false);
    }
  };

  // Finaliza o protocolo (se ainda não foi finalizado) e, na sequência, envia os
  // documentos pra API de GED. Se o protocolo já tiver sido finalizado numa
  // tentativa anterior (documentoProtocoloId já em state) e só o anexo tiver
  // falhado, pula a finalização de novo — refinalizar dá erro ("já foi finalizado").
  const handleEnviarProtocolo = async (requerimentoFile, outrosArquivos) => {
    if (!requerimentoFile) {
      setError('O arquivo do requerimento é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let documentoProtocoloIdAtual = documentoProtocoloId;
      let dadosAtualizados = protocoloData;

      if (!documentoProtocoloIdAtual) {
        const finalizeResponse = await protocoloService.finalizarProtocoloOnline(solicitacaoId, user);
        const finalizeResult = finalizeResponse.data || finalizeResponse;
        documentoProtocoloIdAtual = finalizeResult?.documentoProtocoloId;
        dadosAtualizados = { ...protocoloData, ...finalizeResult };

        setDocumentoProtocoloId(documentoProtocoloIdAtual);
        setProtocoloData(dadosAtualizados);
        localStorage.setItem('protocolo_rascunho', JSON.stringify({
          solicitacaoId,
          requerimentoId,
          documentoProtocoloId: documentoProtocoloIdAtual
        }));
      }

      const { usuarioId } = protocoloService.extrairIdsUsuario(user);

      await documentoAnexoService.anexarDocumentosProtocoloOnline({
        requerimentoId,
        protocoloId: documentoProtocoloIdAtual,
        usuarioId,
        requerimentoFile,
        outrosArquivos
      });

      setCurrentStep(3);
      localStorage.removeItem('protocolo_rascunho');
    } catch (err) {
      logger.error('Erro ao enviar protocolo:', err);
      setError(err.message || 'Erro ao enviar protocolo');
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNovoProtocolo = () => {
    setCurrentStep(1);
    setSolicitacaoId(null);
    setRequerimentoId(null);
    setDocumentoProtocoloId(null);
    setProtocoloData(null);
    setError(null);
    localStorage.removeItem('protocolo_rascunho');
  };

  // Tela de loading enquanto verifica autenticação
  if (!user) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <p>Carregando...</p>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  // Dados da OAB são salvos separados do usuário no login (authService.getOABData()),
  // não vêm dentro de "user".
  const oabData = authService.getOABData();
  const isInscrito = oabData?.isInscrito || false;
  const tipoProtocolo = isInscrito ? 'Protocolo Online (Inscrito)' : 'Protocolo Online (Não Inscrito)';

  return (
    <MainLayout>
      <PageContainer>
        <Container>
          <Header>
            <Title>Protocolo Online</Title>
            <Subtitle>
              {tipoProtocolo} - {isInscrito
                ? 'Para advogados e estagiários inscritos na OAB/MS'
                : 'Para pessoas físicas não inscritas na OAB/MS'}
            </Subtitle>
          </Header>

          {/* Container para scroll suave do alerta de erro */}
          <div ref={alertRef}>
            {error && (
              <Alert type="error" message={error} onClose={() => setError(null)} />
            )}
          </div>

          {/* Indicador de etapas (esconde na tela de sucesso) */}
          {currentStep !== 3 && (
            <StepIndicator currentStep={currentStep} />
          )}

          {currentStep === 1 && (
            <IniciarProtocolo
              user={user}
              oabData={oabData}
              tipoProtocolo={tipoProtocolo}
              isInscrito={isInscrito}
              loading={loading}
              onIniciar={handleIniciarProtocolo}
            />
          )}

          {currentStep === 2 && (
            <DocumentUpload
              protocoloData={protocoloData}
              loading={loading}
              onEnviar={handleEnviarProtocolo}
              onVoltar={handleVoltar}
            />
          )}

          {currentStep === 3 && (
            <SuccessScreen
              protocoloData={protocoloData}
              onNovoProtocolo={handleNovoProtocolo}
            />
          )}
        </Container>
      </PageContainer>
    </MainLayout>
  );
};

export default ProtocoloOnlinePage;
